import { useState, useEffect } from "react";
import {
  Calendar,
  Clock,
  User,
  Heart,
  Share2,
  ArrowLeft,
  Video,
} from "lucide-react";
import { useParams, useLocation } from "react-router-dom";
import { fetchClassDetails } from "./fetchClasses";
import ClassBooking from "./ClassBooking";
import SEO from "../SEO";
import {
  getClassStatus,
  getCurrentLiveSession,
  getNextUpcomingSessionTime,
  getTimeUntilNextSession,
  isSessionLive,
  isSessionPassed,
  isClassBookable,
} from "../../utils/sessionTiming";
interface ClassData {
  _id: string;
  classTitle: string;
  classDescription: string;
  classStartDate: string;
  classEndDate: string;
  classDuration: string;
  classCategory: string;
  SerialCode: string;
  mentorId: string;
  classThumbnail: string;
  googleMeetLinks: Array<{
    url: string;
    time: string;
  }>;
  createAt: string;
  updateAt: string;
  classPrice: {
    session: string;
    monthly: string;
    yearly: string;
  };
  purchaseStatus: string;
  bookingId?: string; // Add this to handle booking ID from API
  pricingType?: string;
  demoDuration?: string;
  schedule?: {
    [day: string]: {
      isActive: boolean;
    };
  };
}

interface quickInfoInstructor {
  name: string;
  image: string;
  certification: string[] | string | undefined; // Updated to handle different types
  experience: string;
  languages?: string | string[];
}

interface BookingData {
  _id: string;
  classId: string;
  userId: string;
  status: "pending_payment" | "confirmed" | "cancelled" | "failed";
  isDemo: boolean;
  demoCompleted: boolean;
  bookingId: string;
}

interface UserDetails {
  name: string;
  email: string;
  age: string;
  phone: string;
}

interface classDetail {
  class: ClassData;
  instructor: quickInfoInstructor;
  userExists: boolean;
  booking: BookingData | null;
  demoTaken?: boolean;
  userDetails?: UserDetails;
}

interface ApiResponse {
  success: boolean;
  message?: string;
}

export default function ClassDetailsPage() {
  const [classData, setClassData] = useState<ClassData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isBookmarked, setIsBookmarked] = useState<boolean>(false);
  const [mentorData, setMentorData] = useState<quickInfoInstructor>();
  const [googleLinks, setGoogleLinks] = useState<string[]>([]);
  const [sessionTime, setSessionTime] = useState<string[]>([]);
  const [isUserExists, setIsUserExists] = useState<boolean>(false);
  const [demoTaken, setDemoTaken] = useState<boolean>(false);
  const [userDetails, setUserDetails] = useState<UserDetails | null>(null);
  const [classStatus, setClassStatus] = useState<{
    status: string;
    message: string;
  } | null>(null);

  // Updated booking state management
  const [booking, setBooking] = useState<BookingData | null>(null);
  const [showBookingForm, setShowBookingForm] = useState<boolean>(false);
  const [showCertificateModal, setShowCertificateModal] = useState<boolean>(false);
  const [selectedCertificate, setSelectedCertificate] = useState<string[]>([]);

  const param = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const vendorId = queryParams.get("vendorId");

  // Helper function to normalize certification data
  const normalizeCertifications = (
    cert: string[] | string | undefined
  ): string[] => {
    if (!cert) return [];
    if (Array.isArray(cert)) return cert;
    if (typeof cert === "string") return [cert];
    return [];
  };

  const getCertificateUrl = (cert: string): string => {
    const trimmed = (cert || "").trim();
    if (!trimmed) return "";
    if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) return trimmed;
    // Fallback for legacy/local uploads
    return `${window.location.origin}/uploads/${trimmed}`;
  };

  const isPdfUrl = (url: string): boolean => Boolean(url && typeof url === "string" && url.toLowerCase().includes(".pdf"));

  const handleFetchDetail = async () => {
    try {
      setLoading(true);
      setError(null);

      const rawClassId = param.userId || param.classId;

      if (!rawClassId) {
        throw new Error("Class ID not found");
      }

      let decodedClassId = rawClassId;
      try {
        const first = atob(rawClassId);
        try {
          const second = atob(first);
          decodedClassId = /^[0-9a-fA-F]{24}$/.test(second)
            ? second
            : (/^[0-9a-fA-F]{24}$/.test(first) ? first : second || rawClassId);
        } catch {
          decodedClassId = /^[0-9a-fA-F]{24}$/.test(first) ? first : rawClassId;
        }
      } catch {
        decodedClassId = rawClassId;
      }

      const response = (await fetchClassDetails(decodedClassId)) as classDetail;

      if (response && response.class) {
        const formatMeetTime = (value?: string) =>
          value
            ? value.replace(/undefined/gi, '').replace(/\s+/g, ' ').replace(/\s+,/g, ',').trim()
            : 'TBD';

        setClassData(response.class);
        const meetLinks = Array.isArray(response.class.googleMeetLinks)
          ? response.class.googleMeetLinks
          : [];
        setGoogleLinks(meetLinks.map((link) => link.url));
        setSessionTime(meetLinks.map((times) => formatMeetTime(times.time)));

        // Set user existence
        setIsUserExists(response.userExists);

        // Set booking ID from backend response
        setBooking(response.booking || null);
        setDemoTaken(response.demoTaken || false);
        setUserDetails(response.userDetails || null);

        if (response.class.mentorId && response.instructor) {
          setMentorData({
            name: response.instructor.name || "Unknown Instructor",
            image: response.instructor.image,
            certification: response.instructor.certification, // Keep original data
            experience:
              response.instructor.experience || "No experience listed",
            languages: response.instructor.languages || "No languages listed",
          });
        }
      } else {
        throw new Error("No data received from API");
      }
    } catch (error) {
      console.error("Error fetching class data:", error);
      setError(
        error instanceof Error ? error.message : "Failed to fetch class data"
      );
      setClassData(null);
    } finally {
      setLoading(false);
    }
  };

  // Fetch class data from backend
  useEffect(() => {
    handleFetchDetail();
  }, [param.userId, param.classId]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (classData) {
        const status = getClassStatus(classData, sessionTime);
        setClassStatus(status);
      }
    }, 5001); // Update every 5 seconds

    return () => clearInterval(interval);
  }, [classData]);

  // Handle successful booking - update the backend booking ID
  const handleBookingSuccess = (newBookingId: string) => {
    handleFetchDetail();
    setShowBookingForm(false);
  };

  // Check if user has a valid booking (user exists AND backend has booking ID)
  const hasValidBooking =
    isUserExists &&
    booking &&
    booking.status === "confirmed" &&
    !booking.isDemo;
  const hasActiveDemo =
    isUserExists && booking && booking.isDemo && !booking.demoCompleted;

  // Removed localized functions: timeToMinutes, getNextUpcomingSessionTime, getCurrentLiveSession, getTimeUntilNextSession, getClassStatus, isSessionLive, isSessionPassed

  // Format date for display (DD:MM:YYYY)
  const formatDateOnly = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB"); // DD/MM/YYYY format
  };

  // Format date for display with day name
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getDayOfWeek = (date: Date) => {
    const days = [
      "sunday",
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
    ];
    return days[date.getDay()];
  };

  const handleBack = (): void => {
    window.history.back();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 via-teal-50 to-white">
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-teal-500"></div>
        </div>
      </div>
    );
  }

  if (error || !classData) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 via-teal-50 to-white">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {error ? "Error Loading Class" : "Class Not Found"}
            </h2>
            <p className="text-gray-600 mb-4">
              {error || "The requested class could not be found."}
            </p>
            <button
              onClick={handleBack}
              className="px-6 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  const liveSession = getCurrentLiveSession(sessionTime);
  const nextSession = getNextUpcomingSessionTime(classData, sessionTime);

  if (!classStatus) {
    const status = getClassStatus(classData, sessionTime);
    setClassStatus(status);
    return null;
  }

  const getPriceDisplayInfo = () => {
    const isPaid =
      classData?.classPrice &&
      (Number(classData.classPrice.session) > 0 ||
        Number(classData.classPrice.monthly) > 0 ||
        Number(classData.classPrice.yearly) > 0);

    if (classData?.pricingType === "demo" && isPaid) {
      return {
        priceText: `₹${classData.classPrice?.session || 0}`,
        type: "Premium Class",
        hasDemo: true,
      };
    }
    if (classData?.pricingType === "demo") {
      return { priceText: "Demo", type: "Demo Class", hasDemo: false };
    }
    if (classData?.purchaseStatus === "Free Tier" || !isPaid) {
      return { priceText: "Free", type: "Free Class", hasDemo: false };
    }
    return {
      priceText: `₹${classData?.classPrice?.session || 0}`,
      type: "Premium Class",
      hasDemo: false,
    };
  };

  const priceInfo = getPriceDisplayInfo();

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 via-teal-50 to-white">
      <SEO 
        title={classData.classTitle} 
        description={classData.classDescription} 
        image={classData.classThumbnail} 
        url={window.location.href} 
        keywords={`${classData.classTitle}, ${classData.classCategory}`}
      />
      {/* Header - Fixed z-index to prevent overlap */}
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={handleBack}
              className="flex items-center text-gray-600 hover:text-teal-600 transition-colors"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Back to Classes
            </button>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setIsBookmarked(!isBookmarked)}
                className={`p-2 rounded-full transition-colors ${isBookmarked
                  ? "text-red-500 bg-red-50"
                  : "text-gray-400 hover:text-red-500"
                  }`}
              >
                <Heart
                  className={`h-5 w-5 ${isBookmarked ? "fill-current" : ""}`}
                />
              </button>
              <button className="p-2 rounded-full text-gray-400 hover:text-teal-600 transition-colors">
                <Share2 className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content - Add proper margin-top */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden relative">
              <div className="relative">
                {classData.classThumbnail ? (
                  <img
                    src={classData.classThumbnail}
                    alt={classData.classTitle}
                    className="w-full h-64 object-cover"
                  />
                ) : (
                  <div className="h-64 bg-gradient-to-r from-teal-400 via-teal-500 to-green-400"></div>
                )}
                <div className="absolute inset-0 bg-black bg-opacity-30"></div>
                <div className="absolute bottom-6 left-6 text-white">
                  <div className="flex items-center space-x-2 mb-3">
                    <span className="inline-block px-3 py-1 bg-white bg-opacity-20 rounded-full text-sm font-medium">
                      {classData.classCategory}
                    </span>
                    <span
                      className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${classData.purchaseStatus === "Free Tier"
                        ? "bg-green-500 bg-opacity-80"
                        : "bg-blue-500 bg-opacity-80"
                        }`}
                    >
                      {classData.purchaseStatus}
                    </span>
                    {hasValidBooking && (
                      <span className="inline-block px-3 py-1 bg-green-600 bg-opacity-80 rounded-full text-sm font-medium">
                        ✓ Booked
                      </span>
                    )}
                    {liveSession && hasValidBooking && (
                      <span className="inline-block px-3 py-1 bg-red-500 bg-opacity-80 rounded-full text-sm font-medium animate-pulse">
                        🔴 LIVE
                      </span>
                    )}
                  </div>
                  <h1 className="text-3xl font-bold mb-2">
                    {classData.classTitle}
                  </h1>
                  <div className="flex items-center space-x-4 text-white text-opacity-90">
                    <div className="flex items-center">
                      <User className="h-4 w-4 mr-1" />
                      {mentorData?.name || "Loading..."}
                    </div>
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 mr-1" />
                      {nextSession
                        ? `Next: ${nextSession.time}`
                        : formatDate(classData.classStartDate)}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Reorganized Info Cards */}
            <div className="shadow-lg rounded-lg">
              <div className="bg-white p-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {/* Start Date */}
                  <div className="text-center p-4 bg-gray-50 rounded-xl">
                    <Calendar className="h-6 w-6 text-gray-600 mx-auto mb-2" />
                    <div className="text-sm text-gray-600">Start Date</div>
                    <div className="font-semibold text-gray-900">
                      {formatDateOnly(classData.classStartDate)}
                    </div>
                  </div>
                  {/* End Date */}
                  <div className="text-center p-4 bg-gray-50 rounded-xl">
                    <Calendar className="h-6 w-6 text-gray-600 mx-auto mb-2" />
                    <div className="text-sm text-gray-600">End Date</div>
                    <div className="font-semibold text-gray-900 text-xs">
                      {classData.classEndDate ? formatDateOnly(classData.classEndDate) : "This session does not have ending date"}
                    </div>
                  </div>

                  {/* Weekly Schedule Card */}
                  {classData.schedule && (
                    <div className="text-center p-4 bg-indigo-50 rounded-xl">
                      <Calendar className="h-6 w-6 text-indigo-600 mx-auto mb-2" />
                      <div className="text-sm text-gray-600">
                        Weekly Schedule
                      </div>
                      <div className="font-semibold text-gray-900 text-xs flex flex-wrap justify-center gap-1 mt-1">
                        {Object.entries(classData.schedule)
                          .filter(([, details]) => details.isActive)
                          .map(([day]) => {
                            const isToday =
                              getDayOfWeek(new Date()) === day.toLowerCase();
                            return (
                              <span
                                key={day}
                                className={`px-1.5 py-0.5 rounded ${isToday
                                  ? "bg-indigo-200 text-indigo-800"
                                  : "bg-gray-200"
                                  }`}
                              >
                                {day.substring(0, 3)}
                              </span>
                            );
                          })}
                      </div>
                    </div>
                  )}

                  {/* Duration */}
                  <div className="text-center p-4 bg-teal-50 rounded-xl">
                    <Clock className="h-6 w-6 text-teal-600 mx-auto mb-2" />
                    <div className="text-sm text-gray-600">Duration</div>
                    <div className="font-semibold text-gray-900">
                      {classData.classDuration}
                    </div>
                  </div>

                  {/* Sessions per day */}
                  <div className="text-center p-4 bg-blue-50 rounded-xl">
                    <Video className="h-6 w-6 text-blue-600 mx-auto mb-2" />
                    <div className="text-sm text-gray-600">Sessions/Day</div>
                    <div className="font-semibold text-gray-900">
                      {sessionTime?.length || 0}
                    </div>
                  </div>

                  {/* Prices Card */}
                  {(classData?.classPrice?.session ||
                    classData?.classPrice?.monthly ||
                    classData?.classPrice?.yearly) && (
                      <div className="p-4 bg-purple-50 rounded-xl">
                        <h4 className="font-semibold text-center text-gray-900 mb-2">
                          Pricing
                        </h4>
                        <div className="space-y-1 text-sm text-center">
                          {classData.classPrice?.session && (
                            <div>
                              Session:{" "}
                              <span className="font-semibold">
                                ₹{classData.classPrice.session}
                              </span>
                            </div>
                          )}
                          {classData.classPrice?.monthly && (
                            <div>
                              Monthly:{" "}
                              <span className="font-semibold">
                                ₹{classData.classPrice.monthly}
                              </span>
                            </div>
                          )}
                          {classData.classPrice?.yearly && (
                            <div>
                              Yearly:{" "}
                              <span className="font-semibold">
                                ₹{classData.classPrice.yearly}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                  {/* Demo Duration */}
                  {classData.pricingType === "demo" &&
                    classData.demoDuration && (
                      <div className="text-center p-4 bg-yellow-50 rounded-xl">
                        <Calendar className="h-6 w-6 text-yellow-600 mx-auto mb-2" />
                        <div className="text-sm text-gray-600">
                          Demo Duration
                        </div>
                        <div className="font-semibold text-gray-900">
                          {classData.demoDuration} days
                        </div>
                      </div>
                    )}
                  {/* Next Session */}
                  {nextSession && (
                    <div className="text-center p-4 bg-green-50 rounded-xl">
                      <Calendar className="h-6 w-6 text-green-600 mx-auto mb-2" />
                      <div className="text-sm text-gray-600">Next Session</div>
                      <div className="font-semibold text-gray-900">
                        {nextSession.time}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Session Status */}
              <div className="bg-white p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Session Status
                </h3>
                <div className="p-4 rounded-xl bg-gradient-to-r from-teal-50 to-green-50">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold text-gray-900">
                        {classStatus.message}
                      </h4>
                      {nextSession && (
                        <p className="text-gray-500 text-sm mt-1">
                          Next: {nextSession.time} ({getTimeUntilNextSession(classData, sessionTime)})
                        </p>
                      )}
                      {hasValidBooking && booking && (
                        <p className="text-green-600 text-xs mt-1">
                          Booking ID: {booking.bookingId}
                        </p>
                      )}
                    </div>
                    <div
                      className={`px-3 py-1 rounded-full text-sm font-medium ${classStatus.status === "live"
                        ? "bg-red-100 text-red-800"
                        : classStatus.status === "upcoming"
                          ? "bg-blue-100 text-blue-800"
                          : "bg-gray-100 text-gray-800"
                        }`}
                    >
                      {classStatus.status === "live"
                        ? "Live Now"
                        : classStatus.status === "upcoming"
                          ? "Upcoming"
                          : "No Sessions"}
                    </div>
                  </div>
                </div>
              </div>

              {/* About This Class */}
              <div className="bg-white p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  About This Class
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {classData.classDescription}
                </p>
              </div>

              {/* Session Links */}
              {classData.googleMeetLinks &&
                classData.googleMeetLinks.length > 0 && (
                  <div className="bg-white p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">
                      Session Links
                    </h3>
                    <div className="space-y-3">
                      {(() => {
                        if (!isUserExists) {
                          return (
                            <div className="text-center p-6 bg-blue-50 rounded-lg">
                              <h4 className="font-medium text-gray-900 mb-2">
                                Login Required
                              </h4>
                              <p className="text-gray-600 text-sm mb-4">
                                Please log in to access session links and join
                                live classes
                              </p>
                              <a
                                href="/login"
                                className="inline-block px-6 py-2 bg-teal-500 text-white text-sm rounded-lg hover:bg-teal-600 transition-colors"
                              >
                                Login Now
                              </a>
                            </div>
                          );
                        }

                        const hasJoinableLinks =
                          classData.googleMeetLinks &&
                          classData.googleMeetLinks.some((link) => link.url);

                        if (
                          (hasValidBooking || hasActiveDemo) &&
                          hasJoinableLinks
                        ) {
                          return classData.googleMeetLinks.map(
                            (meetLink, index) => {
                              const timeStr =
                                sessionTime[index] || meetLink.time;
                              const isLive = isSessionLive(timeStr, sessionTime);
                              const isPast = isSessionPassed(timeStr);
                              const isNext = nextSession?.time === timeStr;

                              return (
                                <div
                                  key={index}
                                  className={`flex items-center justify-between p-3 rounded-lg ${isLive
                                    ? "bg-red-50 border border-red-200"
                                    : isNext
                                      ? "bg-green-50 border border-green-200"
                                      : isPast
                                        ? "bg-gray-50"
                                        : "bg-blue-50 border border-blue-200"
                                    }`}
                                >
                                  <div className="flex items-center">
                                    <Video
                                      className={`h-5 w-5 mr-3 ${isLive
                                        ? "text-red-600"
                                        : isNext
                                          ? "text-green-600"
                                          : isPast
                                            ? "text-gray-400"
                                            : "text-teal-600"
                                        }`}
                                    />
                                    <div>
                                      <span className="font-medium text-gray-900">
                                        Slot {index + 1}
                                        {isLive && (
                                          <span className="ml-2 text-red-600 text-sm">
                                            🔴 LIVE
                                          </span>
                                        )}
                                        {isNext && !isLive && (
                                          <span className="ml-2 text-green-600 text-sm">
                                            ⏰ NEXT
                                          </span>
                                        )}
                                      </span>
                                      <div className="text-sm text-gray-600">
                                        {timeStr}
                                        {isNext && (
                                          <span className="ml-2 text-green-600 font-medium">
                                            ({getTimeUntilNextSession(classData, sessionTime)})
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                  <a
                                    href={meetLink.url || "#"}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={`px-4 py-2 text-sm rounded-lg transition-colors ${isLive
                                      ? "bg-red-500 text-white hover:bg-red-600 animate-pulse"
                                      : isNext
                                        ? "bg-green-500 text-white hover:bg-green-600"
                                        : isPast
                                          ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                                          : "bg-teal-500 text-white hover:bg-teal-600"
                                      }`}
                                    onClick={
                                      isPast
                                        ? (e) => e.preventDefault()
                                        : undefined
                                    }
                                  >
                                    {isLive
                                      ? "Join Live"
                                      : isNext
                                        ? "Join Next"
                                        : isPast
                                          ? "Ended"
                                          : "Join Meeting"}
                                  </a>
                                </div>
                              );
                            }
                          );
                        }

                        return (
                          <div className="text-center p-6 bg-orange-50 rounded-lg">
                            <h4 className="font-medium text-gray-900 mb-2">
                              {isClassBookable(classData, sessionTime) ? "Booking Required" : "Class Ended"}
                            </h4>
                            <p className="text-gray-600 text-sm mb-4">
                              {isClassBookable(classData, sessionTime) 
                                ? "Please book this class to access session links and join live classes"
                                : "This class has ended and is no longer accepting new bookings."}
                            </p>
                            {isClassBookable(classData, sessionTime) ? (
                              <button
                                onClick={() => setShowBookingForm(true)}
                                className="inline-block px-6 py-2 bg-orange-500 text-white text-sm rounded-lg hover:bg-orange-600 transition-colors"
                              >
                                Book Now
                              </button>
                            ) : (
                              <button
                                disabled
                                className="inline-block px-6 py-2 bg-gray-400 text-white text-sm rounded-lg cursor-not-allowed"
                              >
                                Class Expired
                              </button>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                )}
            </div>

            {/* Instructor Section */}
            {mentorData && (
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  Meet Your Instructor
                </h3>
                <div className="flex items-start space-x-4">
                  {mentorData.image ? (
                    <img
                      src={mentorData.image}
                      alt={mentorData.name}
                      className="w-16 h-16 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 text-xl font-bold shrink-0">
                      {mentorData.name
                        ?.split(" ")
                        .map((n: string) => n[0])
                        .join("")
                        .substring(0, 2)
                        .toUpperCase() || "IN"}
                    </div>
                  )}
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900">
                      {mentorData.name}
                    </h4>
                    <p className="text-sm text-gray-600 mb-2">
                      {mentorData.experience || "Experienced Instructor"}
                    </p>
                    {mentorData.certification &&
                      normalizeCertifications(mentorData.certification).length >
                      0 && (
                        <div className="mb-3">
                          <button
                            onClick={() => {
                              setSelectedCertificate(
                                normalizeCertifications(mentorData.certification)
                                  .map((c) => String(c).trim())
                                  .filter(Boolean)
                              );
                              setShowCertificateModal(true);
                            }}
                            className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white text-sm rounded-lg transition-colors"
                          >
                            View Certificates
                          </button>
                        </div>
                      )}
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {mentorData.languages
                        ? `Languages: ${Array.isArray(mentorData.languages)
                          ? mentorData.languages.join(", ")
                          : mentorData.languages
                        }`
                        : "No languages listed"}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar - Fixed z-index and positioning */}
          <div className="space-y-6 sticky self-start max-h-[calc(100vh-6rem)] overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]" style={{ top: "6rem" }}>
            {/* Booking Card - Fixed sticky positioning with proper z-index */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="text-center mb-6">
                <div className="text-3xl font-bold text-gray-900 mb-1">
                  {priceInfo.priceText}
                </div>
                <div className="text-gray-600">{priceInfo.type}</div>
                {priceInfo.hasDemo && (
                  <div className="text-sm text-green-600 mt-1">
                    Demo is available for this class
                  </div>
                )}

                {/* Booking Status */}
                {hasValidBooking && (
                  <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
                    <div className="text-xs text-green-600 mb-1">
                      ✓ Booked Successfully
                    </div>
                    <div className="text-sm font-semibold text-green-900">
                      Booking ID: {booking?.bookingId}
                    </div>
                  </div>
                )}

                {/* Next Session Info */}
                {nextSession && (
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                    <div className="text-xs text-blue-600 mb-1">
                      Next Session
                    </div>
                    <div className="text-sm font-semibold text-blue-900">
                      {nextSession.time}
                    </div>
                    <div className="text-sm text-blue-700">
                      {getTimeUntilNextSession(classData, sessionTime)}
                    </div>
                  </div>
                )}

                {/* Price Options for Premium Classes */}
                {priceInfo.type === "Premium Class" && (
                  <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                    <div className="text-xs text-gray-600 mb-2">
                      Pricing Options:
                    </div>
                    <div className="space-y-1 text-sm">
                      {classData?.classPrice?.session && (
                        <div className="flex justify-between">
                          <span>Session:</span>
                          <span className="font-semibold">
                            ₹{classData.classPrice.session}
                          </span>
                        </div>
                      )}
                      {classData?.classPrice?.monthly && (
                        <div className="flex justify-between">
                          <span>Monthly:</span>
                          <span className="font-semibold">
                            ₹{classData.classPrice.monthly}
                          </span>
                        </div>
                      )}
                      {classData?.classPrice?.yearly && (
                        <div className="flex justify-between">
                          <span>Yearly:</span>
                          <span className="font-semibold">
                            ₹{classData.classPrice.yearly}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Updated Button Logic Based on Requirements */}
              <div className="space-y-4">
                {/* Case 1: User exists AND has a confirmed, non-demo booking */}
                {hasValidBooking && (
                  <>
                    {liveSession && (
                      <a
                        href={googleLinks[liveSession.index]}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full bg-gradient-to-r from-red-500 to-red-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-red-600 hover:to-red-700 transform hover:scale-105 transition-all duration-200 shadow-lg block text-center animate-pulse"
                      >
                        🔴 Join Live Session
                      </a>
                    )}
                    {!liveSession && nextSession && (
                      <a
                        href={googleLinks[nextSession.index]}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full bg-gradient-to-r from-green-500 to-green-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-green-600 hover:to-green-700 transform hover:scale-105 transition-all duration-200 shadow-lg block text-center"
                      >
                        ⏰ Join Next Session ({nextSession.time})
                      </a>
                    )}
                    {!liveSession && !nextSession && (
                      <button className="w-full bg-gradient-to-r from-teal-500 to-green-500 text-white py-3 px-6 rounded-lg font-semibold hover:from-teal-600 hover:to-green-600 transform hover:scale-105 transition-all duration-200 shadow-lg">
                        View All Sessions
                      </button>
                    )}
                  </>
                )}

                {/* Case 2: User has a completed demo, needs to pay */}
                {isUserExists && booking && booking.isDemo && (
                  isClassBookable(classData, sessionTime) ? (
                    <button
                      onClick={() => setShowBookingForm(true)}
                      className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-orange-600 hover:to-orange-700 transform hover:scale-105 transition-all duration-200 shadow-lg"
                    >
                      Purchase Now
                    </button>
                  ) : (
                    <button
                      disabled
                      className="w-full bg-gray-400 text-white py-3 px-6 rounded-lg font-semibold cursor-not-allowed shadow-lg"
                    >
                      Class Expired
                    </button>
                  )
                )}

                {/* Case 3: User has an active demo */}
                {hasActiveDemo && (
                  <>
                    {liveSession && (
                      <a
                        href={googleLinks[liveSession.index]}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full bg-gradient-to-r from-red-500 to-red-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-red-600 hover:to-red-700 transform hover:scale-105 transition-all duration-200 shadow-lg block text-center animate-pulse"
                      >
                        Join Demo Session
                      </a>
                    )}
                  </>
                )}

                {/* Case 4: User exists but NO booking - Show Book Now button */}
                {isUserExists && !booking && (
                  isClassBookable(classData, sessionTime) ? (
                    <button
                      onClick={() => setShowBookingForm(true)}
                      className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-orange-600 hover:to-orange-700 transform hover:scale-105 transition-all duration-200 shadow-lg"
                    >
                      📅 Book Now
                    </button>
                  ) : (
                    <button
                      disabled
                      className="w-full bg-gray-400 text-white py-3 px-6 rounded-lg font-semibold cursor-not-allowed shadow-lg"
                    >
                      Class Expired
                    </button>
                  )
                )}

                {/* Case 5: User does NOT exist - Show Book Now button */}
                {!isUserExists && (
                  isClassBookable(classData, sessionTime) ? (
                    <button
                      onClick={() => {
                        console.log('Login to Book Class clicked');
                        setShowBookingForm(true);
                      }}
                      type="button"
                      className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-blue-600 hover:to-blue-700 transform hover:scale-105 transition-all duration-200 shadow-lg"
                    >
                      Book Now
                    </button>
                  ) : (
                    <button
                      disabled
                      className="w-full bg-gray-400 text-white py-3 px-6 rounded-lg font-semibold cursor-not-allowed shadow-lg"
                    >
                      Class Expired
                    </button>
                  )
                )}
              </div>

              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between items-center text-gray-600">
                    <span>Class Duration</span>
                    <span className="font-semibold text-gray-900">
                      {classData.classDuration}
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-gray-600">
                    <span>Category</span>
                    <span className="font-semibold text-gray-900">
                      {classData.classCategory}
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-gray-600">
                    <span>Total Sessions</span>
                    <span className="font-semibold text-gray-900">
                      {sessionTime?.length || 0}
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-gray-600">
                    <span>Status</span>
                    <span
                      className={`font-semibold ${hasValidBooking ? "text-green-600" : "text-orange-600"
                        }`}
                    >
                      {hasValidBooking ? "Booked" : "Available"}
                    </span>
                  </div>
                  {nextSession && (
                    <div className="flex justify-between items-center text-gray-600">
                      <span>Next Session</span>
                      <span className="font-semibold text-gray-900">
                        {nextSession.time}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Quick Info */}
            <div className="bg-gradient-to-br from-teal-50 to-green-50 rounded-2xl p-6">
              <h4 className="font-semibold text-gray-900 mb-4">
                What You'll Need
              </h4>
              <div className="space-y-3 text-sm">
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-teal-500 rounded-full mr-3"></div>
                  <span className="text-gray-700">
                    Stable internet connection
                  </span>
                </div>
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-teal-500 rounded-full mr-3"></div>
                  <span className="text-gray-700">
                    {(classData?.classCategory || "").includes("Yoga")
                      ? "Yoga mat and comfortable space"
                      : (classData?.classCategory || "").includes("Meditation")
                        ? "Quiet space for meditation"
                        : "Appropriate equipment and space"}
                  </span>
                </div>
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-teal-500 rounded-full mr-3"></div>
                  <span className="text-gray-700">Water bottle and towel</span>
                </div>
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-teal-500 rounded-full mr-3"></div>
                  <span className="text-gray-700">
                    Comfortable workout clothing
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ClassBooking Modal/Component - Fixed z-index */}
      {showBookingForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <ClassBooking
              classData={classData}
              onClose={() => setShowBookingForm(false)}
              onBookingSuccess={handleBookingSuccess}
              ClassId={param.userId || param.classId || ""}
              userDetails={userDetails}
              demoCompleted={booking?.demoCompleted || false}
              vendorId={vendorId}
            />
          </div>
        </div>
      )}

      {/* Certificate Modal */}
      {showCertificateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold text-gray-800">Instructor Certifications</h2>
                <button
                  onClick={() => setShowCertificateModal(false)}
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                >
                  ×
                </button>
              </div>

              <div className="bg-gradient-to-r from-teal-50 to-blue-50 p-6 rounded-xl border border-teal-200">
                <div className="space-y-3">
                  {selectedCertificate.map((cert: string, index: number) => {
                    const url = getCertificateUrl(cert);
                    const showPreview = Boolean(url);
                    const isPdf = url ? isPdfUrl(url) : false;

                    return (
                      <div
                        key={index}
                        className="flex items-start gap-3 p-4 bg-white rounded-lg shadow-sm border-l-4 border-teal-600"
                      >
                        <div className="text-2xl text-teal-600 flex-shrink-0">✓</div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <p className="text-gray-800 font-semibold">
                                Certificate {index + 1}
                              </p>
                              <p className="text-gray-600 text-sm break-all">
                                {cert.trim()}
                              </p>
                            </div>
                            {url && (
                              <a
                                href={url}
                                target="_blank"
                                rel="noreferrer"
                                className="px-3 py-1.5 bg-teal-600 hover:bg-teal-700 text-white text-xs rounded-md transition-colors flex-shrink-0"
                              >
                                Open
                              </a>
                            )}
                          </div>

                          {showPreview && (
                            <div className="mt-3">
                              {isPdf ? (
                                <iframe
                                  src={url}
                                  className="w-full h-64 rounded-lg border border-gray-200 bg-white"
                                  title={`Certificate PDF ${index + 1}`}
                                />
                              ) : (
                                <img
                                  src={url}
                                  alt={`Certificate ${index + 1}`}
                                  className="w-full max-h-64 object-contain rounded-lg border border-gray-200 bg-white"
                                  onError={(e) => {
                                    // Hide broken images instead of showing a giant broken icon
                                    e.currentTarget.style.display = "none";
                                  }}
                                  loading="lazy"
                                />
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="flex gap-3 justify-end mt-6">
                <button
                  onClick={() => setShowCertificateModal(false)}
                  className="px-6 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg font-medium transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
